import { useState, useRef, useEffect, useMemo } from 'react';
// EXISTING import - add URL analysis parameter
import { URLAnalysis } from '../../services/api/sharepointService';
import { 
  SharePointData, 
  ExcludedItems, 
  SharePointItem, 
  DrillDownState, 
  SharePointListItem,
  PaginationInfo,
  DEFAULT_DRILL_DOWN_STATE,
  DEFAULT_PAGINATION
} from '../../types/sharepoint.types';
import { sharepointTabConfig } from "../../constants/sharepointTabConfig";
import { 
  createNewSharePoint, 
  reorderSharePoints, 
  reorderSharePointsByDrag,
  fetchSharePointItems,
  getCachedData
} from './sharepointHelpers';
import { 
  fetchListItemsWithNextLink, 
  fetchDriveItems,
  NavigationHistoryUtils,
  convertToPaginationInfo,
  DEFAULT_PAGE_SIZE
} from '../../services/api/sharepointService';

interface ContextMenu {
  visible: boolean;
  x: number;
  y: number;
  sharepointId: number;
}

interface UseSharePointDataProps {
  sharepoints: SharePointData[];
  setSharepoints: React.Dispatch<React.SetStateAction<SharePointData[]>>;
  updateExcludedItems: (sharepointId: number, type: keyof ExcludedItems, itemId: string) => void;
  handleToggleChange: (key: 'lists' | 'document_libraries', sharepointId: number) => void;
  assistant: any;
  triggerAutoSave: () => void;
  isSharePointFullLoad: boolean;
  setPendingSave: (value: boolean) => void;
  connectedSharePointIds: string[];
  setConnectedSharePointIds: React.Dispatch<React.SetStateAction<string[]>>;
}

export const useSharePointData = ({
  sharepoints,
  setSharepoints,
  updateExcludedItems,
  handleToggleChange,
  assistant,
  triggerAutoSave,
  isSharePointFullLoad,
  setPendingSave,
  connectedSharePointIds,
  setConnectedSharePointIds
}: UseSharePointDataProps) => {
  const [selectedSharePoint, setSelectedSharePoint] = useState<number>(1);
  const [activeSubTabs, setActiveSubTabs] = useState<keyof typeof sharepointTabConfig>('lists');
  const [expandedGetData, setExpandedGetData] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [showDocumentCache, setShowDocumentCache] = useState(false);
  const [isLoadingTabs, setIsLoadingTabs] = useState<{ [sharepointId: number]: { [key: string]: boolean } }>({});
  const [checkedItems, setCheckedItems] = useState<string[]>(() => {
    return sharepoints.flatMap((sharepoint) =>
      sharepoint.sharepointItems
        ? [
            ...(sharepoint.sharepointItems.lists || []),
            ...(sharepoint.sharepointItems.document_libraries || []),
          ]
            .filter((item) => item.checked)
            .map((item) => item.id.toString())
        : []
    );
  });
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [contextMenu, setContextMenu] = useState<ContextMenu>({
    visible: false,
    x: 0,
    y: 0,
    sharepointId: 0
  });

  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);
  const [showHowImage, setShowHowImage] = useState(false);
  const [modalSize, setModalSize] = useState('max-w-[40rem]');
  const [imageSrc, setImageSrc] = useState("");
  const [modalRefreshNModal, setModalRefreshNModal] = useState(false);
  const [connectUsing, setConnectUsing] = useState<string>('loop');
  const [lastSaveTarget, setLastSaveTarget] = useState<'azure' | 'azure'>('azure');
  const [systemConfig, setSystemConfig] = useState<{ Id: string; ConfigValue: string; }[]>([]);

  useEffect(() => {
    const config = getCachedData('systemConfig');
    if (config) {
      setSystemConfig(config);
    }
  }, []);

  useEffect(() => {
    const configValue = systemConfig.find(item => item.Id === 'document_cache_enabled_sharepoint')?.ConfigValue;
    const defaultDocumentCache = Boolean(configValue);
    setShowDocumentCache(defaultDocumentCache);
  }, [systemConfig]);

  const updateSharePointState = (
    sharepointId: number, 
    updates: Partial<SharePointData>
  ) => {
    setSharepoints(prev => prev.map(s =>
      s.id === sharepointId
        ? { ...s, ...updates }
        : s
    ));
  };

  const updateDrillDownState = (
    sharepointId: number, 
    updates: Partial<DrillDownState>
  ) => {
    setSharepoints(prev => prev.map(s =>
      s.id === sharepointId
        ? {
            ...s,
            drillDownState: {
              ...DEFAULT_DRILL_DOWN_STATE,
              ...s.drillDownState,
              ...updates
            }
          }
        : s
    ));
  };

  const getAllDocumentIdsFromTree = (items: SharePointListItem[]): string[] => {
    const documentIds: string[] = [];
    
    const collectIds = (itemList: SharePointListItem[]) => {
      itemList.forEach(item => {
        documentIds.push(item.id);
        if (item.is_folder && item.children) {
          collectIds(item.children as SharePointListItem[]);
        }
      });
    };
    
    collectIds(items);
    return documentIds;
  };

  const handleDocumentExclusion = (
  sharepointId: number,
  documentId: string,
  libraryId: string,
  exclude: boolean
) => {
  setSharepoints(prev => prev.map(s => {
    if (s.id !== sharepointId) return s;
    
    const currentExcluded = s.excludedItems;
    let updatedPartialExcluded = [...(currentExcluded.partial_excluded_document_libraries || [])];
    let updatedExcludedLibraries = [...currentExcluded.excluded_document_libraries];
    const isLibraryExcluded = currentExcluded.excluded_document_libraries.includes(libraryId);
    
    const libraryIndex = updatedPartialExcluded.findIndex(lib => lib.lib_id === libraryId);
    
    if (isLibraryExcluded) {
      // NEW LOGIC: Library is excluded - handle explicit inclusions/exclusions
      if (libraryIndex === -1) {
        if (!exclude) {
          // Document being included in excluded library - create partial config
          // Get all document IDs from current tree view
          const currentTreeItems = s.drillDownState?.items || [];
          const allDocumentIds = getAllDocumentIdsFromTree(currentTreeItems);
          const otherDocumentIds = allDocumentIds.filter(id => id !== documentId);
          
          if (otherDocumentIds.length === 0) {
            // All documents are included - remove library from excluded entirely
            updatedExcludedLibraries = updatedExcludedLibraries.filter(id => id !== libraryId);
            // No partial config needed
          } else {
            updatedPartialExcluded.push({
              lib_id: libraryId,
              excluded_docs: otherDocumentIds // Exclude all others, include only this one
            });
            
            // CRITICAL FIX: Remove library from excluded_document_libraries
            updatedExcludedLibraries = updatedExcludedLibraries.filter(id => id !== libraryId);
          }
        }
        // If exclude=true, do nothing (document follows library exclusion)
      } else {
        const currentLib = updatedPartialExcluded[libraryIndex];
        let updatedExcludedDocs = [...currentLib.excluded_docs];
        
        if (exclude) {
          // Add to excluded list (explicitly exclude this document)
          if (!updatedExcludedDocs.includes(documentId)) {
            updatedExcludedDocs.push(documentId);
          }
        } else {
          // Remove from excluded list (explicitly include this document)
          updatedExcludedDocs = updatedExcludedDocs.filter(id => id !== documentId);
        }
        
        // CRITICAL CHECK: If all documents are now included, remove partial config entirely
        if (updatedExcludedDocs.length === 0) {
          // Remove entire partial config - everything is included
          updatedPartialExcluded.splice(libraryIndex, 1);
          // Also ensure library is not in excluded list
          updatedExcludedLibraries = updatedExcludedLibraries.filter(id => id !== libraryId);
        } else {
          updatedPartialExcluded[libraryIndex] = {
            ...currentLib,
            excluded_docs: updatedExcludedDocs
          };
          
          // CRITICAL FIX: Remove library from excluded_document_libraries when creating partial config
          updatedExcludedLibraries = updatedExcludedLibraries.filter(id => id !== libraryId);
        }
      }
    } else {
      // EXISTING LOGIC: Library is included - handle exclusions normally
      if (libraryIndex === -1) {
        if (exclude) {
          updatedPartialExcluded.push({
            lib_id: libraryId,
            excluded_docs: [documentId]
          });
        }
      } else {
        const currentLib = updatedPartialExcluded[libraryIndex];
        let updatedExcludedDocs = [...currentLib.excluded_docs];
        
        if (exclude) {
          if (!updatedExcludedDocs.includes(documentId)) {
            updatedExcludedDocs.push(documentId);
          }
        } else {
          updatedExcludedDocs = updatedExcludedDocs.filter(id => id !== documentId);
        }
        
        // CRITICAL CHECK: If all documents are now included, remove partial config entirely
        if (updatedExcludedDocs.length === 0) {
          // Remove entire partial config - everything is included
          updatedPartialExcluded.splice(libraryIndex, 1);
        } else {
          updatedPartialExcluded[libraryIndex] = {
            ...currentLib,
            excluded_docs: updatedExcludedDocs
          };
        }
      }
    }
    
    return {
      ...s,
      excludedItems: {
        ...currentExcluded,
        excluded_document_libraries: updatedExcludedLibraries,
        partial_excluded_document_libraries: updatedPartialExcluded
      }
    };
  }));
  
  triggerAutoSave();
};


const handleLibraryExclusion = (
  sharepointId: number,
  libraryId: string,
  exclude: boolean
) => {
  setSharepoints(prev => {
    const newSharepoints = prev.map(s => {
      if (s.id !== sharepointId) return s;
      
      const currentExcluded = s.excludedItems;
      let updatedExcludedLibraries = [...currentExcluded.excluded_document_libraries];
      let updatedPartialExcluded = [...(currentExcluded.partial_excluded_document_libraries || [])];
      
      if (exclude) {
        // Exclude entire library
        if (!updatedExcludedLibraries.includes(libraryId)) {
          updatedExcludedLibraries.push(libraryId);
        }
        // Remove any partial configurations for this library
        updatedPartialExcluded = updatedPartialExcluded.filter(lib => lib.lib_id !== libraryId);
      } else {
        // Include entire library
        // Remove library from excluded list
        updatedExcludedLibraries = updatedExcludedLibraries.filter(id => id !== libraryId);
        // Remove any partial configurations for this library
        updatedPartialExcluded = updatedPartialExcluded.filter(lib => lib.lib_id !== libraryId);
      }
      
      const newExcludedItems = {
        excluded_lists: [...(currentExcluded.excluded_lists || [])],
        excluded_document_libraries: updatedExcludedLibraries,
        partial_excluded_document_libraries: updatedPartialExcluded
      };
      
      return {
        ...s,
        excludedItems: newExcludedItems
      };
    });
    
    return newSharepoints;
  });
  
  triggerAutoSave();
};
  const isDocumentExcluded = (
    documentId: string,
    libraryId: string,
    parentIds: string[] = [],
    sharepoint: SharePointData
  ): boolean => {
    const excludedItems = sharepoint.excludedItems;
    
      // ADD THESE SAFETY CHECKS:
    if (!excludedItems.excluded_document_libraries) {
      excludedItems.excluded_document_libraries = [];
    }
    if (!excludedItems.partial_excluded_document_libraries) {
      excludedItems.partial_excluded_document_libraries = [];
    }
    
    if (excludedItems.excluded_document_libraries.includes(libraryId)) {
      return true;
    }
    
    const partialLibrary = excludedItems.partial_excluded_document_libraries?.find(lib => lib.lib_id === libraryId);
    if (!partialLibrary) return false;
    
    if (partialLibrary.excluded_docs.includes(documentId)) return true;
    
    return parentIds.some(parentId => partialLibrary.excluded_docs.includes(parentId));
  };

  const getLibrarySelectionState = (libraryId: string, sharepoint: SharePointData): 'checked' | 'unchecked' | 'indeterminate' => {
    const excludedItems = sharepoint.excludedItems;
    
    if (excludedItems.excluded_document_libraries.includes(libraryId)) {
      return 'unchecked';
    }
    
    const partialLibrary = excludedItems.partial_excluded_document_libraries?.find(lib => lib.lib_id === libraryId);
    if (!partialLibrary || partialLibrary.excluded_docs.length === 0) {
      return 'checked';
    }
    
    return 'indeterminate';
  };

  const setActiveSubTabsWithReset = (tab: keyof typeof sharepointTabConfig) => {
    if (selectedSharePoint) {
      updateDrillDownState(selectedSharePoint, DEFAULT_DRILL_DOWN_STATE);
    }
    setActiveSubTabs(tab);
  };

  
   // COMPLETE UPDATED handleItemClick function for useSharePointData.ts
const handleItemClick = async (
  item: SharePointItem, 
  itemType: 'lists' | 'document_libraries', 
  sharepointId: number
) => {
  const sharepoint = sharepoints.find(s => s.id === sharepointId);
  if (!sharepoint) {
    console.error('SharePoint not found:', sharepointId);
    return;
  }

  updateDrillDownState(sharepointId, {
    isViewingItems: true,
    selectedItem: item,
    itemType,
    items: [],
    isLoading: true,
    error: undefined,
    pagination: DEFAULT_PAGINATION,
    isLoadingPage: false,
    appliedFilters: undefined,
    navigationHistory: [],
    currentHistoryIndex: -1
  });

  try {
    let fetchedItems: SharePointListItem[] = [];
    let paginationInfo: PaginationInfo = DEFAULT_PAGINATION;
    
    if (itemType === 'lists') {
      const listId = item.list_id || item.id;
      
      const result = await fetchListItemsWithNextLink(
        sharepoint.sharepoint_id,
        listId,
        undefined,
        DEFAULT_PAGE_SIZE,
        undefined
      );
      
      fetchedItems = result.items.map((responseItem, index) => {
        const safeItem = { ...responseItem };
        const itemId = responseItem.id || responseItem.ID || `item-${index}`;
        return {
          ...safeItem,
          id: itemId
        };
      });
      
      const navigationHistory: string[] = [];
      const currentHistoryIndex = -1;
      
      paginationInfo = convertToPaginationInfo(result, navigationHistory, currentHistoryIndex);
      
      updateDrillDownState(sharepointId, {
        isViewingItems: true,
        selectedItem: item,
        itemType,
        items: fetchedItems,
        isLoading: false,
        error: undefined,
        pagination: paginationInfo,
        isLoadingPage: false,
        navigationHistory,
        currentHistoryIndex
      });
      
    } else if (itemType === 'document_libraries') {
      const driveId = item.drive_id || item.library_id || item.id;
      
      // NEW: Extract target folder path from URL analysis
      const urlAnalysis = sharepoint.url_analysis;
      const targetFolderPath = urlAnalysis?.url_type === 'folder' 
        ? urlAnalysis.target_folder_path 
        : undefined;

      // NEW: Log folder targeting info
      if (targetFolderPath) {
        console.log(`🎯 Document library click with folder targeting: ${targetFolderPath}`);
      } else {
        console.log(`📁 Document library click without folder targeting (full library)`);
      }
      
      // ENHANCED: Call fetchDriveItems with optional folder targeting
      fetchedItems = await fetchDriveItems(
        sharepoint.sharepoint_id,
        driveId,
        'app_id_placeholder',
        1000,
        targetFolderPath  // NEW: Pass folder targeting parameter
      );
      
      paginationInfo = {
        ...DEFAULT_PAGINATION,
        itemCount: fetchedItems.length
      };

      updateDrillDownState(sharepointId, {
        isViewingItems: true,
        selectedItem: item,
        itemType,
        items: fetchedItems,
        isLoading: false,
        error: undefined,
        pagination: paginationInfo,
        isLoadingPage: false,
        navigationHistory: [],
        currentHistoryIndex: -1
      });
    }

  } catch (error) {
    console.error('Failed to fetch items:', error);
    
    updateDrillDownState(sharepointId, {
      isViewingItems: true,
      selectedItem: item,
      itemType,
      items: [],
      isLoading: false,
      error: error instanceof Error ? error.message : 'Failed to fetch items',
      pagination: DEFAULT_PAGINATION,
      isLoadingPage: false,
      navigationHistory: [],
      currentHistoryIndex: -1
    });
  }
};

  const handleNavigateNext = async (sharepointId: number): Promise<void> => {
    const sharepoint = sharepoints.find(s => s.id === sharepointId);
    const drillDownState = sharepoint?.drillDownState;
    
    if (!sharepoint || !drillDownState?.selectedItem || !drillDownState.isViewingItems) {
      console.error('Invalid state for next navigation:', { sharepointId, drillDownState });
      return;
    }

    if (drillDownState.itemType !== 'lists') {
      console.warn('Next navigation not supported for document libraries');
      return;
    }

    if (!drillDownState.pagination.hasNextPage && !drillDownState.pagination.canGoForward) {
      console.warn('No next page available');
      return;
    }

    updateDrillDownState(sharepointId, {
      isLoadingPage: true
    });

    try {
      const listId = drillDownState.selectedItem.list_id || drillDownState.selectedItem.id;
      let nextLink: string | undefined;

      if (drillDownState.pagination.canGoForward && drillDownState.navigationHistory.length > 0) {
        const newIndex = drillDownState.currentHistoryIndex + 1;
        nextLink = drillDownState.navigationHistory[newIndex];
      } else if (drillDownState.pagination.nextLink) {
        nextLink = drillDownState.pagination.nextLink;
      }

      if (!nextLink) {
        throw new Error('No nextLink available for navigation');
      }

      const result = await fetchListItemsWithNextLink(
        sharepoint.sharepoint_id,
        listId,
        drillDownState.appliedFilters,
        drillDownState.pagination.pageSize,
        nextLink
      );
      
      const fetchedItems = result.items.map((responseItem, index) => {
        const safeItem = { ...responseItem };
        const itemId = responseItem.id || responseItem.ID || `next-item-${index}`;
        return {
          ...safeItem,
          id: itemId
        };
      });

      let updatedHistory = [...drillDownState.navigationHistory];
      let updatedIndex = drillDownState.currentHistoryIndex;

      if (!drillDownState.pagination.canGoForward) {
        if (result.nextLink) {
          updatedHistory = NavigationHistoryUtils.addToHistory(updatedHistory, result.nextLink);
        }
        updatedIndex = updatedHistory.length - 1;
      } else {
        updatedIndex = drillDownState.currentHistoryIndex + 1;
      }

      const paginationInfo = convertToPaginationInfo(result, updatedHistory, updatedIndex);

      updateDrillDownState(sharepointId, {
        items: fetchedItems,
        pagination: paginationInfo,
        isLoadingPage: false,
        error: undefined,
        navigationHistory: updatedHistory,
        currentHistoryIndex: updatedIndex
      });

    } catch (error) {
      console.error('Failed to navigate next:', error);
      
      updateDrillDownState(sharepointId, {
        isLoadingPage: false,
        error: error instanceof Error ? error.message : 'Failed to load next page'
      });
    }
  };

  const handleNavigatePrevious = async (sharepointId: number): Promise<void> => {
    const sharepoint = sharepoints.find(s => s.id === sharepointId);
    const drillDownState = sharepoint?.drillDownState;
    
    if (!sharepoint || !drillDownState?.selectedItem || !drillDownState.isViewingItems) {
      console.error('Invalid state for previous navigation:', { sharepointId, drillDownState });
      return;
    }

    if (drillDownState.itemType !== 'lists') {
      console.warn('Previous navigation not supported for document libraries');
      return;
    }

    if (!drillDownState.pagination.canGoBack) {
      console.warn('No previous page available');
      return;
    }

    updateDrillDownState(sharepointId, {
      isLoadingPage: true
    });

    try {
      const listId = drillDownState.selectedItem.list_id || drillDownState.selectedItem.id;
      let nextLink: string | undefined;

      if (drillDownState.currentHistoryIndex === 0) {
        nextLink = undefined;
      } else {
        const newIndex = drillDownState.currentHistoryIndex - 1;
        nextLink = NavigationHistoryUtils.getPreviousLink(drillDownState.navigationHistory, drillDownState.currentHistoryIndex);
      }

      const result = await fetchListItemsWithNextLink(
        sharepoint.sharepoint_id,
        listId,
        drillDownState.appliedFilters,
        drillDownState.pagination.pageSize,
        nextLink
      );
      
      const fetchedItems = result.items.map((responseItem, index) => {
        const safeItem = { ...responseItem };
        const itemId = responseItem.id || responseItem.ID || `prev-item-${index}`;
        return {
          ...safeItem,
          id: itemId
        };
      });

      const updatedIndex = Math.max(0, drillDownState.currentHistoryIndex - 1);
      const paginationInfo = convertToPaginationInfo(result, drillDownState.navigationHistory, updatedIndex);

      updateDrillDownState(sharepointId, {
        items: fetchedItems,
        pagination: paginationInfo,
        isLoadingPage: false,
        error: undefined,
        currentHistoryIndex: updatedIndex
      });

    } catch (error) {
      console.error('Failed to navigate previous:', error);
      
      updateDrillDownState(sharepointId, {
        isLoadingPage: false,
        error: error instanceof Error ? error.message : 'Failed to load previous page'
      });
    }
  };

  const handlePageSizeChange = async (
    sharepointId: number,
    pageSize: number
  ): Promise<void> => {
    const sharepoint = sharepoints.find(s => s.id === sharepointId);
    const drillDownState = sharepoint?.drillDownState;
    
    if (!sharepoint || !drillDownState?.selectedItem || !drillDownState.isViewingItems) {
      console.error('Invalid state for page size change:', { sharepointId, drillDownState });
      return;
    }

    if (drillDownState.itemType !== 'lists') {
      console.warn('Page size change not supported for document libraries');
      return;
    }

    updateDrillDownState(sharepointId, {
      isLoadingPage: true
    });

    try {
      const listId = drillDownState.selectedItem.list_id || drillDownState.selectedItem.id;

      const result = await fetchListItemsWithNextLink(
        sharepoint.sharepoint_id,
        listId,
        drillDownState.appliedFilters,
        pageSize,
        undefined
      );
      
      const fetchedItems = result.items.map((responseItem, index) => {
        const safeItem = { ...responseItem };
        const itemId = responseItem.id || responseItem.ID || `size-item-${index}`;
        return {
          ...safeItem,
          id: itemId
        };
      });

      const { history, index } = NavigationHistoryUtils.resetHistory();
      const paginationInfo = convertToPaginationInfo(result, history, index);

      updateDrillDownState(sharepointId, {
        items: fetchedItems,
        pagination: {
          ...paginationInfo,
          pageSize: pageSize
        },
        isLoadingPage: false,
        error: undefined,
        navigationHistory: history,
        currentHistoryIndex: index
      });

    } catch (error) {
      console.error('Failed to change page size:', error);
      
      updateDrillDownState(sharepointId, {
        isLoadingPage: false,
        error: error instanceof Error ? error.message : 'Failed to change page size'
      });
    }
  };

  const handleSearch = async (
    sharepointId: number,
    searchTerm: string,
    searchFields?: string[]
  ): Promise<void> => {
    const sharepoint = sharepoints.find(s => s.id === sharepointId);
    const drillDownState = sharepoint?.drillDownState;
    
    if (!sharepoint || !drillDownState?.selectedItem || !drillDownState.isViewingItems) {
      return;
    }

    const searchFilters: Record<string, any> = {};
    if (searchTerm.trim()) {
      if (searchFields && searchFields.length > 0) {
        searchFields.forEach(field => {
          searchFilters[`${field}_contains`] = searchTerm;
        });
      } else {
        searchFilters['Title_contains'] = searchTerm;
      }
    }

    const { history, index } = NavigationHistoryUtils.resetHistory();
    
    updateDrillDownState(sharepointId, {
      appliedFilters: searchFilters,
      navigationHistory: history,
      currentHistoryIndex: index
    });

    await handleItemClick(drillDownState.selectedItem, drillDownState.itemType, sharepointId);
  };

  const handleBackToLists = (sharepointId: number) => {
    updateDrillDownState(sharepointId, DEFAULT_DRILL_DOWN_STATE);
  };

  const setSelectedSharePointWithReset = (sharepointId: number) => {
    setSharepoints(prev => prev.map(s => ({
      ...s,
      drillDownState: DEFAULT_DRILL_DOWN_STATE
    })));
    
    setSelectedSharePoint(sharepointId);
  };

  const addNewSharePoint = () => {
    const newSharePoint = createNewSharePoint(sharepoints);
    setSharepoints(prev => [...prev, newSharePoint]);
    setSelectedSharePointWithReset(newSharePoint.id);
  };

  const handleUrlChange = (value: string) => {
    setSharepoints(prev => prev.map(sharepoint =>
      sharepoint.id === selectedSharePoint ? { ...sharepoint, url: value } : sharepoint
    ));
  };

  const handleContextMenu = (e: React.MouseEvent, sharepointId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      sharepointId
    });
  };

  const handleCheckboxChange = (itemId: string, itemType: string, sharepointId: number, idField: string) => {
    setSharepoints(prev =>
      prev.map(s =>
        s.id === sharepointId
          ? {
              ...s,
              sharepointItems: s.sharepointItems
                ? {
                    ...s.sharepointItems,
                    [itemType]: s.sharepointItems[itemType as keyof typeof s.sharepointItems]?.map(item => {
                      const fieldValue = item[idField as keyof typeof item];
                      return fieldValue === itemId
                        ? { ...item, checked: !item.checked }
                        : item;
                    }) || [],
                  }
                : {
                    lists: [],
                    document_libraries: [],
                  },
            }
          : s
      )
    );

    setCheckedItems(prevCheckedItems =>
      prevCheckedItems.includes(itemId)
        ? prevCheckedItems.filter(id => id !== itemId)
        : [...prevCheckedItems, itemId]
    );
    triggerAutoSave();
  };

  // In your useSharePointData.ts file, replace the handleInsideToggleChange function with this:
const handleInsideToggleChange = (key: 'lists' | 'document_libraries', sharepointId: number) => {
  console.log('🟡 useSharePointData handleInsideToggleChange:', { key, sharepointId });
  
  // Just call the parent handler - keep it simple like Canvas
  handleToggleChange(key, sharepointId);
  setPendingSave(true);
};
  const moveSharePoint = (sharepointId: number, direction: 'up' | 'down') => {
    const reordered = reorderSharePoints(sharepoints, sharepointId, direction);
    setSharepoints(reordered);
  };

  const deleteSharePoint = (sharepointId: number, onConfirm: () => void) => {
  // First, clean up connectedSharePointIds BEFORE removing from sharepoints array
  const sharepointToDelete = sharepoints.find(s => s.id === sharepointId);
  if (sharepointToDelete?.url) {
    const sharepointid_prefix = systemConfig.find(item => item.Id === 'sharepoint_siteid_prefix')?.ConfigValue;
    
    // Use the same URL segment extraction logic as in handleConnect
    let sharepointUrlSegment = sharepointToDelete.url.trim().split('/').filter(Boolean).pop();
    if (sharepointUrlSegment) {
      if (sharepointUrlSegment.length < 7) {
        sharepointUrlSegment = sharepointid_prefix + sharepointUrlSegment;
      }
      const finalSegment = sharepointUrlSegment;
      
      // Remove from connectedSharePointIds
      setConnectedSharePointIds(prev =>
        prev.filter(id => id !== finalSegment)
      );
    }
  }

  // Then remove from sharepoints array
  setSharepoints(prev => {
    const filtered = prev.filter(s => s.id !== sharepointId);
    return filtered.map((sharepoint, index) => ({
      ...sharepoint,
      order: index + 1
    }));
  });

  // Handle selected SharePoint reset
  if (selectedSharePoint === sharepointId) {
    const remainingSharepoints = sharepoints.filter(s => s.id !== sharepointId);
    setSelectedSharePointWithReset(remainingSharepoints[0]?.id || 0);
  }
triggerAutoSave(); 
  onConfirm();
};

  const handleDragStart = (e: React.DragEvent, position: number) => {
    e.stopPropagation();
    dragItem.current = position;
    e.currentTarget.classList.add('opacity-50');
  };

  const handleDragEnter = (e: React.DragEvent, position: number) => {
    e.preventDefault();
    e.stopPropagation();
    dragOverItem.current = position;
    e.currentTarget.classList.add('bg-gray-50');
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.currentTarget.classList.remove('bg-gray-50');
  };

  const handleDragEnd = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('opacity-50');

    if (dragItem.current !== null && dragOverItem.current !== null) {
      const reordered = reorderSharePointsByDrag(sharepoints, dragItem.current, dragOverItem.current);
      setSharepoints(reordered);
    }

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const setActiveTab = (sharepointId: number, tab: 'CONNECT' | 'PREVIEW') => {
    setSharepoints(prev => prev.map(sharepoint =>
      sharepoint.id === sharepointId 
        ? { 
            ...sharepoint, 
            activeTab: tab,
            drillDownState: DEFAULT_DRILL_DOWN_STATE
          } 
        : sharepoint
    ));
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>, sharepointId: number) => {
    const updatedSharepointName = event.target.value;
    setSharepoints(prevSharepoints =>
      prevSharepoints.map(sharepoint =>
        sharepoint.id === sharepointId
          ? { ...sharepoint, sharepoint_name: updatedSharepointName }
          : sharepoint
      )
    );
  };

  const toggleExpandRow = (idx: number) => {
    setExpandedRows(prev => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  const handleRefresh = async (showConfirmationModal: (message: string, title: string, onConfirm: () => void) => void) => {
    showConfirmationModal(
      "This action will fetch the SharePoint data again, and it might take a few minutes. Do you want to proceed?", 
      "Refresh Preview Confirmation", 
      async () => {
        if (isRefreshing) return;
        setIsRefreshing(true);
        if (selectedSharePoint) {
          updateDrillDownState(selectedSharePoint, DEFAULT_DRILL_DOWN_STATE);
          
          try {
            const sharepoint = sharepoints.find(s => s.id === selectedSharePoint);
            const urlAnalysis = sharepoint?.url_analysis || {
              url_type: 'site',
              target_list_id: undefined,
              target_library_id: undefined,
              target_folder_path: undefined
            };

            await fetchSharePointItems(
              selectedSharePoint,
              sharepoints,
              setSharepoints,
              setCheckedItems,
              setIsLoadingTabs,
              assistant,
              true,
              urlAnalysis // NEW: Pass URL analysis for targeting
            );
          } catch (error) {
            console.error('Refresh failed:', error);
          } finally {
            setIsRefreshing(false);
          }
        }
      }
    );
  };

 
 const selectedSharepointData = sharepoints.find(s => s.id === selectedSharePoint);

// Add this debug to verify the fix
console.log('🎯 DIRECT selectedSharepointData excluded libs:', 
  selectedSharepointData?.excludedItems?.excluded_document_libraries?.length || 0);

   return {
    selectedSharePoint,
    setSelectedSharePoint: setSelectedSharePointWithReset,
    activeSubTabs,
    setActiveSubTabs: setActiveSubTabsWithReset,
    expandedGetData,
    setExpandedGetData,
    expandedRows,
    showDocumentCache,
    isLoadingTabs,
    setIsLoadingTabs,
    checkedItems,
    setCheckedItems,
    isRefreshing,
    contextMenu,
    setContextMenu,
    showHowImage,
    setShowHowImage,
    modalSize,
    setModalSize,
    imageSrc,
    setImageSrc,
    modalRefreshNModal,
    setModalRefreshNModal,
    connectedSharePointIds,
    setConnectedSharePointIds,
    connectUsing,
    setConnectUsing,
    lastSaveTarget,
    systemConfig,
    selectedSharepointData,

    addNewSharePoint,
    handleUrlChange,
    handleContextMenu,
    handleCheckboxChange,
    handleInsideToggleChange,
    moveSharePoint,
    deleteSharePoint,
    handleDragStart,
    handleDragEnter,
    handleDragLeave,
    handleDragEnd,
    handleDragOver,
    setActiveTab,
    handleNameChange,
    toggleExpandRow,
    handleRefresh,

    handleItemClick,
    handleBackToLists,
    handleNavigateNext,
    handleNavigatePrevious,
    handlePageSizeChange,
    handleSearch,

    handleDocumentExclusion,
    handleLibraryExclusion,
    isDocumentExcluded,
    getLibrarySelectionState,

    dragItem,
    dragOverItem
  };
};